import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { MemberApi, type DashboardData, type NextPayoutInfo } from '../services';
import { useAuth } from './AuthContext';

interface MemberContextValue {
  dashboard: DashboardData | null;
  dashboardLoading: boolean;
  dashboardError: string | null;
  refreshDashboard: () => Promise<void>;
  nextPayout: NextPayoutInfo | null;
  nextPayoutLoading: boolean;
  nextPayoutError: string | null;
  refreshNextPayout: () => Promise<void>;
}

const MemberContext = createContext<MemberContextValue | undefined>(undefined);

export const MemberProvider = ({ children }: PropsWithChildren) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState<boolean>(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  const [nextPayout, setNextPayout] = useState<NextPayoutInfo | null>(null);
  const [nextPayoutLoading, setNextPayoutLoading] = useState<boolean>(false);
  const [nextPayoutError, setNextPayoutError] = useState<string | null>(null);

  const refreshDashboard = useCallback(async () => {
    if (!isAuthenticated) {
      setDashboard(null);
      setDashboardError(null);
      return;
    }

    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const data = await MemberApi.getDashboard();
      setDashboard(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load dashboard data';
      setDashboardError(message);
    } finally {
      setDashboardLoading(false);
    }
  }, [isAuthenticated]);

  const refreshNextPayout = useCallback(async () => {
    if (!isAuthenticated) {
      setNextPayout(null);
      setNextPayoutError(null);
      return;
    }

    setNextPayoutLoading(true);
    setNextPayoutError(null);
    try {
      const data = await MemberApi.getNextPayout();
      setNextPayout(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load payout info';
      setNextPayoutError(message);
    } finally {
      setNextPayoutLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (isAuthenticated) {
      void refreshDashboard();
      void refreshNextPayout();
    } else {
      setDashboard(null);
      setDashboardError(null);
      setNextPayout(null);
      setNextPayoutError(null);
    }
  }, [authLoading, isAuthenticated, refreshDashboard, refreshNextPayout]);

  const value = useMemo<MemberContextValue>(
    () => ({
      dashboard,
      dashboardLoading,
      dashboardError,
      refreshDashboard,
      nextPayout,
      nextPayoutLoading,
      nextPayoutError,
      refreshNextPayout,
    }),
    [
      dashboard,
      dashboardLoading,
      dashboardError,
      refreshDashboard,
      nextPayout,
      nextPayoutLoading,
      nextPayoutError,
      refreshNextPayout,
    ],
  );

  return <MemberContext.Provider value={value}>{children}</MemberContext.Provider>;
};

export const useMember = () => {
  const context = useContext(MemberContext);

  if (!context) {
    throw new Error('useMember must be used within a MemberProvider');
  }

  return context;
};

